import React, { useRef, useState, MouseEvent } from 'react';
import Button from '../Button/Button';
import { Typography } from '../Typography/Typography';
import Dropdown, { IOption } from '../Dropdown/Dropdown';
import { useClickOutside } from '../../hooks/useClickOutside';
import classes from 'classnames/bind';

const cx = classes.bind(styles);

import styles from './DropdownButton.module.scss';
import { Ellipsis } from '../../assets/icons/Ellipsis';
import { ArrowDownFilled } from '../../assets/icons/ArrowDownFilled';
import { ArrowDown } from '../../assets/icons/ArrowDown';

export interface IDropdownButtonProps {
  text: string;
  primary?: boolean;
  variant?: 'arrow' | 'solid' | 'ellipsis' | 'outline';
  options: IOption[];
  onButtonClick?: () => void;
}

const DropdownButton = ({ text, primary = true, options, onButtonClick, variant = 'solid' }: IDropdownButtonProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const nodeRef = useRef(null);
  const domNode = useClickOutside<HTMLDivElement>(() => setIsDropdownOpen(false));
  const withDropdown = options.length > 0;
  const isHeader = variant === 'arrow' || variant === 'ellipsis';

  const handleArrowClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setIsDropdownOpen((prevState: boolean) => !prevState);
  };

  return (
    <div className={styles['dropdown-button-container']}>
      <Button
        primary={primary}
        onClick={onButtonClick}
        aria-label="dropdown-button"
        className={cx('dropdown-button', variant)}
      >
        <div
          className={cx('dropdown-button-main', {
            'dropdown-button-right': withDropdown,
          })}
        >
          <Typography className={cx('text', variant)} size={isHeader ? 'XXL' : 'M'} weight="semi-bold">
            {text}
          </Typography>
        </div>
        {withDropdown && (
          <div
            role="button"
            ref={domNode}
            className={styles['dropdown-button-arrow']}
            aria-label={`dropdown-${variant}-button`}
            onClick={(event: MouseEvent<HTMLElement>) => handleArrowClick(event)}
          >
            {variant === 'ellipsis' ? (
              <Ellipsis />
            ) : isHeader ? (
              <ArrowDownFilled className={cx('arrow', variant)} />
            ) : (
              <ArrowDown />
            )}
          </div>
        )}
      </Button>
      {withDropdown && (
        <div className={styles['dropdown-container']}>
          <Dropdown position={isHeader ? 'bottom' : 'right'} options={options} ref={nodeRef} isOpen={isDropdownOpen} />
        </div>
      )}
    </div>
  );
};

export default DropdownButton;
